import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Skeleton, Space, Table, Tag } from "antd"
import InvoiceDateDisplay from "./InvoiceDateDisplay"
import InvoiceNumberDisplay from "./InvoiceNumberDisplay"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { useInvoiceSummariesForJobQuery } from "../../../domain/invoicing/invoicingQueries"
import BalanceDisplay from "./BalanceDisplay"
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { InvoiceSummary } from "../../../domain/invoicing/invoice"
import { EmailStatusDisplay } from "./EmailStatusDisplay"
import SubtotalDisplay from "./SubtotalDisplay"
import InvoiceDetails from "./InvoiceDetails"
import { InvoicingSystem } from "../../../domain/invoicing/settings/invoicingSettings"
import QuickBooksTrackingState from "./QuickBooksTrackingState"
import { useContext } from "react"
import { AuthenticationContext } from "../../../AuthenticationContext"
import { shouldShowTrackingColumn } from "../../../domain/invoicing/quickBooksTrackingModel"
import StatementInvoiceDetails from "./StatementInvoiceDetails"

type Props = {
  jobId: number
  preventExports: boolean
}

const InvoiceListTable = ({ jobId, preventExports }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const summariesQuery = useInvoiceSummariesForJobQuery(jobId)

  if (summariesQuery.isLoading) {
    return <Skeleton active />
  }

  const summaries = summariesQuery.data ?? []
  const showInvoicingSystem = summaries.length > 1 && summaries.some(x => x.invoicingSystem !== summaries[0].invoicingSystem)
  const isTenantConnected = currentUser?.isTenantConnectedToQuickBooks === true
  const showQuickBooksColumn = shouldShowTrackingColumn(isTenantConnected, summaries)

  return <Table
    size="small"
    dataSource={summaries}
    rowKey={x => x.invoiceId}
    columns={[
      {
        title: 'Created On',
        render: (_, x) => <InvoiceDateDisplay date={x.createdAt} />
      },
      {
        title: 'Due On',
        render: (_, x) => x.dueOn ? <InvoiceDateDisplay date={x.dueOn} /> : ''
      },
      {
        title: 'Invoice #',
        render: (_, x) => <Space>
          <InvoiceNumberDisplay invoiceSummary={x} />
          {x.statementId && <Tag color="blue">Statement</Tag>}
        </Space>
      },
      ...(showInvoicingSystem ? [{
        title: 'Invoicing System',
        render : (_: any, x: InvoiceSummary) => invoicingSystemNames[x.invoicingSystem]
      }] : []),
      {
        title: 'Emailed',
        render: (_, x) => <EmailStatusDisplay invoiceSummary={x} />
      },
      {
        title: 'Subtotal',
        align: 'right',
        render: (_, x) => <SubtotalDisplay invoiceSummary={x} />
      },
      {
        title: 'Tax',
        align: 'right',
        render: (_, x) => centsToDollarsDisplay(x.salesTax)
      },
      {
        title: 'Total',
        align: 'right',
        render: (_, x) => centsToDollarsDisplay(x.total)
      },
      {
        title: 'Balance',
        align: 'right',
        render: (_, x) => <BalanceDisplay
          balance={x.balance}
          isFullyPaid={x.balance === 0}
          hasPaymentProblem={x.hasPaymentProblem}
          hasPendingPayment={x.hasPendingPayment}
        />
      },
      ...(showQuickBooksColumn ? [{
        title: 'QuickBooks',
        render: (_: any, x: InvoiceSummary) => <QuickBooksTrackingState invoiceSummary={x} />
      }] : []),
    ]}
    expandable={{
      expandedRowRender: x => x.statementId
        ? <StatementInvoiceDetails statementId={x.statementId} currentInvoiceId={x.invoiceId} />
        : <InvoiceDetails invoiceSummary={x} preventExports={preventExports} />,
      expandIcon: ({ expandable, expanded, onExpand, record }) => {
        if (!expandable) {
          return <></>
        }
        const icon = expanded ? faChevronDown : faChevronRight
        const style = { cursor: 'pointer', padding: '0 8px'}
        return <div onClick={e => onExpand(record, e)} style={style}>
          <FontAwesomeIcon icon={icon} />
        </div>
      }
    }}
    pagination={{pageSize: 25, hideOnSinglePage: true}}
    locale={{emptyText: 'No invoices found.'}}
  />
}

export default InvoiceListTable

const invoicingSystemNames: Record<InvoicingSystem, string> = {
  'quickBooks': 'QuickBooks',
  'synkedUp': 'SynkedUP'
}